import type { CdlEventInfo } from '../types/cdl';

/**
 * Single source of truth for the new dc-sci-* tag payloads, keyed by tag index.
 * dc-sci-072 is intentionally not in this catalog — it fires from activities-vehicle-details,
 * a host-agnostic lib that shouldn't depend on an SCI-specific package.
 *
 * The original 20 dc-sci-* tags are not migrated here yet.
 */
export const SCI_CDL_TAGS = {
    'dc-sci-061': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Recent Deals Link',
        location: ['Left Rail Navigation', 'Quick Links'],
        intention: 'View recent deals',
        tagIndex: 'dc-sci-061'
    }),
    'dc-sci-064': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Customer Detail Tile',
        location: ['Left Rail Navigation', 'Build Your Offer'],
        intention: 'View customer details',
        tagIndex: 'dc-sci-064'
    }),
    'dc-sci-065': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Share Deal PDF Link',
        location: ['Customer Bar', 'Share Menu'],
        intention: 'Send Deal PDF to customer',
        tagIndex: 'dc-sci-065'
    }),
    // In-card tab switches, which the entry-point tags dc-sci-064 and dc-sci-062 don't cover.
    'dc-sci-067': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Buyer Details Tab',
        location: ['Customer Details', 'Buyer Details'],
        intention: 'View or edit buyer details',
        tagIndex: 'dc-sci-067'
    }),
    'dc-sci-068': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Insurance Tab',
        location: ['Customer Details', 'Insurance Details'],
        intention: 'View or edit insurance details',
        tagIndex: 'dc-sci-068'
    }),
    'dc-sci-077': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Request Changes/Edit Deal Button',
        location: ['Deal Summary', 'Offer Document'],
        intention: 'Request edits to deal',
        tagIndex: 'dc-sci-077'
    }),
    'dc-sci-078': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Accept Deal Button',
        location: ['Deal Summary', 'Offer Document'],
        intention: 'Accept deal from deal summary',
        tagIndex: 'dc-sci-078'
    }),
    'dc-sci-094': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Offer Updates Available Button',
        location: ['Deal Summary', 'Offer Updates Notification'],
        intention: 'Refresh offer information',
        tagIndex: 'dc-sci-094'
    }),
    'dc-sci-098': (): CdlEventInfo => ({
        action: 'Click',
        element: 'Back to My Deal Button',
        location: ['Digital Deal Jacket', 'Navigation'],
        intention: 'Navigate back to deal information',
        tagIndex: 'dc-sci-098'
    }),
    // Workflow section "View" tags — fire once per route/step entry, distinct from the click tags
    // that navigate there (dc-sci-064/062/059/060/etc).
    'dc-sci-073': (): CdlEventInfo => ({
        action: 'View',
        element: 'Deal Summary Workflow Display',
        location: ['Deal Summary', 'Workflow Section Display'],
        intention: 'Deal summary displayed to the user',
        tagIndex: 'dc-sci-073'
    }),
    'dc-sci-074': (): CdlEventInfo => ({
        action: 'View',
        element: 'Browse Inventory Workflow Display',
        location: ['Browse Inventory', 'Workflow Section Display'],
        intention: 'Inventory search displayed to the user',
        tagIndex: 'dc-sci-074'
    }),
    'dc-sci-075': (): CdlEventInfo => ({
        action: 'View',
        element: 'Manage Documents Workflow Display',
        location: ['Manage Documents', 'Workflow Section Display'],
        intention: 'Document management displayed to the user',
        tagIndex: 'dc-sci-075'
    }),
    'dc-sci-089': (role: 'Buyer' | 'Co-Buyer'): CdlEventInfo => ({
        action: 'Click',
        element: `Add ${role} Billing Address Icon`,
        location: ['Financing', 'Buyer', 'Information'],
        intention: `Add ${role.toLowerCase()} billing address`,
        tagIndex: 'dc-sci-089'
    }),
    'dc-sci-090': (role: 'Buyer' | 'Co-Buyer'): CdlEventInfo => ({
        action: 'Click',
        element: `${role} Housing & Employment Information Accordion`,
        location: ['Financing', 'Buyer', 'Information'],
        intention: `Open ${role.toLowerCase()} Housing & Employment Information Accordion`,
        tagIndex: 'dc-sci-090'
    }),
    'dc-sci-091': (role: 'Buyer' | 'Co-Buyer'): CdlEventInfo => ({
        action: 'Click',
        element: `Add ${role} Previous Employment Information Icon`,
        location: ['Financing', 'Buyer', 'Information'],
        intention: `Open ${role.toLowerCase()} form fields for previous employment information`,
        tagIndex: 'dc-sci-091'
    }),
    'dc-sci-092': (role: 'Buyer' | 'Co-Buyer'): CdlEventInfo => ({
        action: 'Click',
        element: `Add ${role} Additional Income Information Icon`,
        location: ['Financing', 'Buyer', 'Information'],
        intention: `Open ${role.toLowerCase()} form fields for additional income information`,
        tagIndex: 'dc-sci-092'
    }),
    'dc-sci-093': (role: 'Buyer' | 'Co-Buyer'): CdlEventInfo => ({
        action: 'Click',
        element: `Add ${role} Previous Address Icon`,
        location: ['Financing', 'Buyer', 'Information'],
        intention: `Open ${role.toLowerCase()} form fields for previous address`,
        tagIndex: 'dc-sci-093'
    })
} as const;
